import React, { Suspense, lazy, useEffect, useState } from "react";
import { Form } from "@picklebet/ui";
import { z } from "zod";
import { useForm, type Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@picklebet/ui";
import cx from "classnames";

const AddressSelect = lazy(() =>
  import("@picklebet/ui").then((m) => ({
    default: m.AddressSelect,
  })),
);
import { NativeSelect } from "@picklebet/ui";
import { getAddressFromPlaceId, getAllCountries } from "@/utilities/address";
import { Input } from "@picklebet/ui";
import { validateProfile } from "../../services/session";
import { toFormErrors, setFormErrors } from "../../utils/errors";
import { NUMERIC } from "@/utilities/regex";
import { Typography } from "@picklebet/ui";
import { useCountry } from "@/hooks/useLocale";
import { isValidPhoneNumber } from "react-phone-number-input";
import { FooterContent } from "./FooterContent";
import { useIsPhoneVerificationEnabled } from "@/features/auth/hooks/useIsPhoneVerificationEnabled";
import { PhoneVerification } from "../PhoneVerification";
import { CustomerLicenceOptions } from "../../stores/authStore";
import { useOtpFlow } from "@/features/auth/hooks/useOtpFlow";
import { Alert } from "@picklebet/ui";
import type { AddressFormValues, StoredFormValues } from "./SignUpForm";
import type { Country } from "react-phone-number-input/input-mobile";

import * as styles from "./SignUpForm.module.scss";

const AU_CODES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const CA_PROVINCES = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "YT", label: "Yukon" },
];

const getOptions = (codes: string[]) =>
  codes.map((code) => ({ value: code, label: code }));

type AddressProps = {
  storedFormValues: StoredFormValues;
  onAddressSelected: (selectedAddress: AddressFormValues) => void;
  onComplete: (
    values: FormValues,
    attributes: { zendeskDomain: string; zendeskKey: string } | null,
  ) => void;
  onNewAddressClick: () => void;
};

const createSchema = (
  isIsleOfMan: boolean,
  isPhoneVerificationEnabled: boolean,
  countryOfResidence: string,
) => {
  const isAuResident = countryOfResidence === "AU";

  return z
    .object({
      phone: z
        .any()
        .transform((val) => (val ?? "") as string)
        .refine(
          (value) => {
            // AU only, skip for IOM
            if (!isIsleOfMan && isAuResident) {
              return value.startsWith("+614") && isValidPhoneNumber(value);
            }
            // Non AU just needs to be valid
            return isValidPhoneNumber(value);
          },
          {
            message:
              !isIsleOfMan && isAuResident
                ? "Please enter a valid Australian mobile number"
                : "Please enter a valid mobile number",
          },
        ),
      phoneVerificationRequestId: z.string().nullable(),
      unit: z.string().optional(),
      country: z.string().min(1, "Country is required"),
      postCode: z.string().min(1, "Postcode is required"),
      state: z.string().trim().min(1, "State is required"),
      suburb: z.string().min(1, "Suburb is required"),
      street: z.string().min(1, "Street name is required"),
      streetNumber: z.string().min(1, "Street number is required"),
    })
    .superRefine((data, ctx) => {
      if (data.country !== countryOfResidence) {
        ctx.addIssue({
          code: "custom",
          message: "Address must match Country of Residence",
          path: ["country"],
        });
      }
      if (data.country === "AU" && !data.state) {
        ctx.addIssue({
          code: "custom",
          message: "State is required",
          path: ["state"],
        });
      }
      if (data.country === "CA" && !data.state) {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a valid province / territory",
          path: ["state"],
        });
      }
      // AU postcode validation is handled by the backend
      if (data.country === "NZ" && !NUMERIC.test(data.postCode)) {
        ctx.addIssue({
          code: "custom",
          message: "Postcode must contain numbers only",
          path: ["postCode"],
        });
      }
      if (
        isAuResident &&
        isPhoneVerificationEnabled &&
        !data.phoneVerificationRequestId
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Mobile verification is required",
          path: ["phone"],
        });
      }
    });
};

type FormValues = z.infer<ReturnType<typeof createSchema>>;

const ADDRESS_FIELDS: (keyof FormValues)[] = [
  "unit",
  "streetNumber",
  "street",
  "suburb",
  "state",
  "postCode",
];

const Address = ({
  storedFormValues,
  onAddressSelected,
  onComplete,
  onNewAddressClick,
}: AddressProps) => {
  const country = useCountry();
  const countryCode = storedFormValues.details?.countryOfResidence ?? country;
  const isAuResident = countryCode === "AU";
  const isIsleOfMan =
    storedFormValues.licence === CustomerLicenceOptions.ISLE_OF_MAN;

  const { isEnabled: isFFEnabled, loading } = useIsPhoneVerificationEnabled({
    // only AU users need verification and are worth reporting
    reportFailures: isAuResident,
  });
  const isEnabled = isFFEnabled && isAuResident;
  const { store } = useOtpFlow("signup");
  const status = store((s) => s.status);

  const [ageError, setAgeError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const addressFilled = selectedAddress || isExpanded;
  const isVerifiedIfRequired = isEnabled ? status === "verified" : true;
  const canContinue = !loading && addressFilled && isVerifiedIfRequired;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(
      createSchema(
        isIsleOfMan,
        isEnabled,
        storedFormValues.details.countryOfResidence,
      ),
    ),
    defaultValues: {
      ...storedFormValues.address,
      phone: storedFormValues.phone,
      phoneVerificationRequestId: storedFormValues.phoneVerificationRequestId,
    },
  });

  const countryFormValue = watch("country");

  useEffect(() => {
    const { unit, streetNumber, street, suburb, state, postCode, country } =
      storedFormValues.address;
    const canRestore =
      !!street && !!streetNumber && !!suburb && !!postCode && !!country;

    if (canRestore) {
      const unitNumber = unit ? `${unit} ` : "";
      const countryCode = country === "AU" ? "" : `, ${country} `;
      const addressLabel = `${unitNumber}${streetNumber} ${street}, ${suburb} ${state} ${postCode}${countryCode}`;

      setSelectedAddress({
        value: "RESTORED_STATE", // dummy value because we already have the address fields
        label: addressLabel,
      });
    }
  }, [storedFormValues]);

  const resetAddressFields = (setValue: any) => {
    ADDRESS_FIELDS.forEach((field) => setValue(field, ""));
    setAgeError(null);
  };

  const applyAddressValues = (setValue: any, address: Partial<FormValues>) => {
    Object.entries(address).forEach(([key, value]) => {
      setValue(key as keyof FormValues, value || "");
    });
  };

  const handleVerificationStatusChange = (requestId: string | null) => {
    if (requestId) {
      setValue("phoneVerificationRequestId", requestId);
      clearErrors("phone");
      document.getElementById("address-select-input")?.focus();
    } else {
      setError("phoneVerificationRequestId", {
        message: "Mobile verification is required.",
      });
    }
  };

  const handleVerificationExpiry = () => {
    if (!watch("phoneVerificationRequestId")) return;
    setValue("phoneVerificationRequestId", null);
    setError("phoneVerificationRequestId", {
      message: "Your code expired, please try again.",
    });
  };

  useEffect(() => {
    // re-run superRefine cross-field rules when country/state change
    const sub = watch((_, { name, type }) => {
      if (type !== "change") {
        return;
      }
      if (name === "country" || name === "state") {
        trigger(["country", "state"]);
        setAgeError(null);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, trigger]);

  return (
    <>
      <Form
        disabled={isSubmitting}
        onSubmit={handleSubmit(async (values) => {
          const [validation, _, attributes] = await validateProfile("", "", {
            dateOfBirth: storedFormValues.details.dateOfBirth,
            ...values,
          });

          if (validation.length > 0) {
            // dateOfBirth has no field on this step, so surface its error on state and as an alert
            const dobError = validation.find(
              (e) => e.propertyPath === "dateOfBirth",
            );
            setAgeError(dobError?.message ?? null);
            if (dobError) {
              setError("state", { type: "manual", message: " " });
            }
            setFormErrors(
              toFormErrors<FormValues>(validation, "profile"),
              setError,
            );
            return;
          }
          setAgeError(null);

          onComplete(values, attributes);
        })}
      >
        <PhoneVerification
          autoFocus
          flow="signup"
          destination={watch("phone")}
          channel="SMS"
          purpose="SignupMobileVerification"
          name="phone"
          control={control}
          clearErrors={(field) => clearErrors(field as Path<FormValues>)}
          onVerified={handleVerificationStatusChange}
          onSessionExpired={handleVerificationExpiry}
          country={countryCode as Country}
          disabled={isSubmitting}
          autoSend={false}
          errorMessage={errors?.phone?.message}
        />
        {isExpanded ? (
          <>
            <NativeSelect
              options={getAllCountries()}
              label={"Country"}
              errorMessage={errors?.country?.message}
              {...register("country")}
            />
            <div className={styles.twoColumn}>
              <Input
                id={"unit"}
                label={"Unit No."}
                {...register("unit")}
                errorMessage={errors?.unit?.message}
              />
              <Input
                id={"streetNumber"}
                label={"Street No."}
                {...register("streetNumber")}
                errorMessage={errors?.streetNumber?.message}
              />
            </div>
            <Input
              id={"street"}
              label={"Street Name"}
              {...register("street")}
              errorMessage={errors?.street?.message}
            />
            <Input
              id={"suburb"}
              label={"Suburb / City"}
              {...register("suburb")}
              errorMessage={errors?.suburb?.message}
            />
            <div className={styles.twoColumn}>
              {countryFormValue === "AU" && (
                <NativeSelect
                  options={getOptions(AU_CODES)}
                  label={"State / Territory"}
                  {...register("state")}
                  errorMessage={errors?.state?.message}
                />
              )}
              {countryFormValue === "CA" && (
                <NativeSelect
                  options={CA_PROVINCES}
                  label={"Province / Territory"}
                  {...register("state")}
                  errorMessage={errors?.state?.message}
                />
              )}
              {!["AU", "CA"].includes(countryFormValue) && (
                <Input
                  id={"state"}
                  label={"State / Region"}
                  {...register("state")}
                  errorMessage={errors?.state?.message}
                />
              )}
              <Input
                id={"postCode"}
                label={"Postcode"}
                inputMode={
                  ["AU", "NZ"].includes(countryFormValue) ? "numeric" : "text"
                }
                {...register("postCode")}
                errorMessage={errors?.postCode?.message}
              />
            </div>
          </>
        ) : (
          <Suspense fallback={<Input loading label="Address" />}>
            <AddressSelect
              inputId="address-select-input"
              label={`Address`}
              placeholder={`Type residential address`}
              errorMessage={ageError || undefined}
              value={selectedAddress}
              onChange={async (option) => {
                setSelectedAddress(option);

                const address = await getAddressFromPlaceId(option);
                resetAddressFields(setValue);
                applyAddressValues(setValue, address);

                // only expand fields if any required field is missing
                const isValid = await trigger([
                  "country",
                  "postCode",
                  "state",
                  "suburb",
                  "street",
                  "streetNumber",
                ]);

                if (!isValid) {
                  setIsExpanded(true);
                } else {
                  // valid address has all required fileds
                  onAddressSelected(address as AddressFormValues);
                }
              }}
              noOptionsMessage={() => {
                return (
                  <div className={styles.emptyAddress}>
                    No address found.{" "}
                    <Button
                      variant={"link"}
                      onClick={() => {
                        resetAddressFields(setValue);
                        clearErrors(ADDRESS_FIELDS);
                        setIsExpanded(true);
                      }}
                    >
                      Fill in manually
                    </Button>
                  </div>
                );
              }}
              country={countryCode ?? undefined}
              isSearchable={!selectedAddress}
              menuIsOpen={selectedAddress ? false : undefined}
              onEdit={() => setIsExpanded(true)}
            />
          </Suspense>
        )}
        {ageError && (
          <Alert color="danger" className={styles.ageAlert} indicator="top">
            {ageError}
          </Alert>
        )}
        {canContinue && (
          <Button
            className={styles.continueButton}
            type={"submit"}
            variant={"primary"}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Continue
          </Button>
        )}
        {addressFilled && (
          <Typography
            className={styles.wrongAddress}
            component={`span`}
            variant="body"
          >
            Wrong address?{" "}
            <Button
              variant={"link"}
              onClick={() => {
                resetAddressFields(setValue);
                setIsExpanded(false);
                setSelectedAddress(null);
                onNewAddressClick();
              }}
            >
              Look up a new address
            </Button>
          </Typography>
        )}
      </Form>
      <FooterContent
        className={cx(styles.footer, {
          [styles.collapsedAddress]: !isExpanded,
        })}
      />
    </>
  );
};

export { Address };
